<template>
  <a-modal
    :open="open"
    :footer="null"
    @cancel="handleClose"
    width="900px"
    class="review-detail-modal"
  >
    <template #title>
      <div class="modal-title">
        후기 상세 정보
      </div>
    </template>
    
    <div class="modal-content" v-if="reviewData">
      <!-- 기본 정보 섹션 -->
      <div class="detail-section">
        <h4 class="section-title">기본 정보</h4>
        
        <div class="detail-grid">
          <div class="detail-item">
            <label>고객명</label>
            <span>{{ reviewData.customerName || '-' }}</span>
          </div>
          <div class="detail-item">
            <label>전화번호</label>
            <span>{{ phoneNumberFormatter(reviewData.phoneNumber) || '-' }}</span>
          </div>
          <div class="detail-item">
            <label>병원명</label>
            <span>{{ reviewData.companyName || '-' }}</span>
          </div>
          <div class="detail-item">
            <label>수술일자</label>
            <span>{{ convertDate(reviewData.surgeryAt) || '-' }}</span>
          </div>
          <div class="detail-item">
            <label>수술명</label>
            <span>{{ reviewData.categoryName || '-' }}</span>
          </div>
          <div class="detail-item">
            <label>만족도</label>
            <span>{{ reviewData.score || '-' }}</span>
          </div>
        </div>
      </div>

      <!-- 키워드 섹션 -->
      <div class="detail-section" v-if="reviewData.satisfactionPoints && reviewData.satisfactionPoints.length > 0">
        <h4 class="section-title">만족 키워드</h4>
        
        <div class="keywords-display">
          <span 
            v-for="(keyword, index) in reviewData.satisfactionPoints" 
            :key="index" 
            class="keyword-tag"
          >
            {{ keyword }}
          </span>
        </div>
      </div>

      <!-- 후기 내용 섹션 -->
      <div class="detail-section">
        <h4 class="section-title">후기 내용</h4>
        
        <div class="content-display">
          <div class="content-text">
            {{ reviewData.content || '내용이 없습니다.' }}
          </div>
          <div class="content-meta">
            <span class="content-length">{{ (reviewData.content || '').length }}자</span>
          </div>
        </div>
      </div>

      <!-- 상태 및 일시 정보 섹션 -->
      <div class="detail-section">
        <h4 class="section-title">상태 정보</h4>
        
        <div class="detail-grid">
          <div class="detail-item">
            <label>현재 상태</label>
            <div :class="['status-chip', getStatusClass(reviewData.reviewStatus)]">
              {{ getStatusText(reviewData.reviewStatus) }}
            </div>
          </div>
          <div class="detail-item">
            <label>등록일시</label>
            <span>{{ convertToLocalTime(reviewData.createAt) || '-' }}</span>
          </div>
        </div>
      </div>

      <!-- 액션 버튼 -->
      <div class="modal-footer">
        <a-button @click="handleClose">닫기</a-button>
        <a-button 
          v-if="!isExposed(reviewData)" 
          type="primary" 
          class="yellow-button"
          @click="handleStatusChange('exposed')"
        >
          노출
        </a-button>
        <a-button 
          v-if="!isHidden(reviewData)" 
          danger
          @click="handleStatusChange('hidden')"
        >
          비노출
        </a-button>
        <!-- <a-button 
          danger 
          @click="handleDelete"
        >
          삭제
        </a-button> -->
      </div>
    </div>
  </a-modal>
</template>

<script>
import { convertToLocalTime, convertDate, phoneNumberFormatter } from '@/utils/utils';

export default {
  name: 'ReviewDetailModal',
  props: {
    open: {
      type: Boolean,
      default: false
    },
    reviewData: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['close', 'delete', 'approve'],
  setup(props, { emit }) {
    // 상태 텍스트 변환
    const getStatusText = (reviewStatus) => {
      switch (reviewStatus) {
        case 'pending':
          return '대기';
        case 'review_requested':
          return '검수요청';
        case 'exposed':
          return '노출중';
        case 'hidden':
          return '비노출';
        default:
          return '검수요청';
      }
    };

    // 상태 클래스 변환
    const getStatusClass = (reviewStatus) => {
      switch (reviewStatus) {
        case 'pending':
          return 'pending';
        case 'review_requested':
          return 'review_requested';
        case 'exposed':
          return 'exposed';
        case 'hidden':
          return 'hidden';
        default:
          return 'pending';
      }
    };

    // 상태 확인 헬퍼 함수들
    const isExposed = (record) => {
      return record.reviewStatus === 'exposed';
    };

    const isHidden = (record) => {
      return record.reviewStatus === 'hidden';
    };

    const isPending = (record) => {
      return record.reviewStatus === 'review_requested';
    };

    // 모달 닫기
    const handleClose = () => {
      emit('close');
    };

    // 삭제 처리
    const handleDelete = () => {
      emit('delete', props.reviewData._id);
    };

    // 상태 변경 처리
    const handleStatusChange = (status) => {
      emit('approve', props.reviewData._id, status === 'exposed');
      emit('close');
    };

    return {
      convertToLocalTime,
      convertDate,
      phoneNumberFormatter,
      getStatusText,
      getStatusClass,
      isExposed,
      isHidden,
      isPending,
      handleClose,
      handleDelete,
      handleStatusChange
    };
  }
};
</script>

<style scoped lang="scss">
.modal-title {
  font-size: 16px;
  font-weight: bold;
}

.modal-content {
  border: none !important;
  box-shadow: none !important;
  padding: 20px;
  max-height: 80vh;
  overflow-y: auto;
}

.detail-section {
  margin-bottom: 32px;
  
  &:last-child {
    margin-bottom: 0;
  }
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #FFCC32;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  
  label {
    font-size: 12px;
    color: #666;
    font-weight: 500;
  }
  
  span {
    font-size: 14px;
    color: #333;
    word-break: break-all;
  }
}

.keywords-display {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.keyword-tag {
  display: inline-block;
  padding: 4px 12px;
  background-color: #f0f8ff;
  color: #1890ff;
  border: 1px solid #d6e4ff;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  
  &:hover {
    background-color: #e6f7ff;
    border-color: #91d5ff;
  }
}

.status-chip {
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  display: inline-block;
  min-width: 70px;
  border: 1px solid transparent;
  
  &.exposed {
    background-color: #f6ffed !important;
    color: #389e0d !important;
    border-color: #b7eb8f !important;
  }
  
  &.review_requested {
    background-color: #fff7e6 !important;
    color: #d46b08 !important;
    border-color: #ffd591 !important;
  }
  
  &.pending {
    background-color: #f9f0ff !important;
    color: #722ed1 !important;
    border-color: #d3adf7 !important;
  }
  
  
  &.hidden {
    background-color: #fff1f0 !important;
    color: #cf1322 !important;
    border-color: #ffa39e !important;
  }
}

.content-display {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  border: 1px solid #e9ecef;
}

.content-text {
  font-size: 14px;
  line-height: 1.6;
  color: #333;
  white-space: pre-wrap;
  word-break: break-word;
  margin-bottom: 12px;
}

.content-meta {
  display: flex;
  justify-content: flex-end;
  
  .content-length {
    font-size: 12px;
    color: #999;
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 24px;
}

:deep(.yellow-button) {
  background-color: #FFCC32 !important;
  border-color: #FFCC32 !important;
  color: #000 !important;
  
  &:hover {
    background-color: #E6B82E !important;
    border-color: #E6B82E !important;
  }
  
  &:disabled {
    background-color: #d9d9d9 !important;
    border-color: #d9d9d9 !important;
    color: rgba(0, 0, 0, 0.25) !important;
  }
}

// 반응형 스타일
@media screen and (max-width: 768px) {
  .modal-content {
    padding: 16px;
  }
  
  .detail-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .section-title {
    font-size: 14px;
    margin-bottom: 12px;
  }
  
  .detail-section {
    margin-bottom: 24px;
  }
  
  .keywords-display {
    gap: 6px;
  }
  
  .modal-footer {
    flex-direction: column;
    
    .ant-btn {
      width: 100%;
    }
  }
}
</style> 